import React from "react";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { linearGradientDef } from "@nivo/core";
import { ResponsiveLine } from "@nivo/line";
import { commaNotation } from "@utils";

const AREA_GRADIENT_ID = "hyro-line-chart-area-gradient";
const AREA_GRADIENT_BOTTOM = "#667EEA";

const pickEdgeAndMidTicks = (data) => {
  if (!data?.length) return [];
  if (data.length <= 3) return data.map((d) => d.x);
  const first = data[0].x;
  const last = data[data.length - 1].x;
  const mid = data[Math.floor(data.length / 2)].x;
  return [first, mid, last];
};

const computeDefaultYScale = (data) => {
  if (!data?.length) return { type: "linear", min: "auto", max: "auto", stacked: false };
  const ys = data.map((d) => d.y);
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const range = max - min || Math.abs(max) || 1;
  return {
    type: "linear",
    min: min - (range * 0.45),
    max,
    stacked: false
  };
};

const HyroLineChart = ({
  data = [],
  height = 80,
  loading = false,
  xTicks,
  gradient = true,
  gradientStops,
  interactive = true,
  showLine = true,
  yScale
}) => {
  const theme = useTheme();

  const isFixedHeight = typeof height === "number";

  if (loading || !data.length) {
    return (
      <Box sx={{ height: isFixedHeight ? height : "100%", width: "100%" }}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  const series = [{ id: "value", data }];
  const ticks = xTicks ?? pickEdgeAndMidTicks(data);
  const effectiveYScale = yScale ?? computeDefaultYScale(data);
  const baseline = typeof effectiveYScale.min === "number"
    ? effectiveYScale.min
    : Math.min(...data.map((d) => d.y));
  const stops = gradientStops ?? [
    { offset: 5, color: theme.palette.secondary[400], opacity: 1 },
    { offset: 95, color: AREA_GRADIENT_BOTTOM, opacity: 0.1 }
  ];
  const layers = ["grid", "markers", "axes", "areas"];
  if (showLine) layers.push("lines");
  layers.push("crosshair", "points", "slices", "mesh", "legends");

  return (
    <Stack gap={1} sx={{ width: "100%", height: isFixedHeight ? "auto" : height, minHeight: 0 }}>
      <Box sx={{ width: "100%", ...(isFixedHeight ? { height } : { flex: 1, minHeight: 0 }) }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 2, right: 0, bottom: 0, left: 0 }}
          xScale={{ type: "point" }}
          yScale={effectiveYScale}
          layers={layers}
          curve="catmullRom"
          colors={[theme.palette.secondary[500]]}
          lineWidth={1}
          enablePoints={false}
          enableArea
          areaOpacity={0.6}
          areaBaselineValue={baseline}
          defs={gradient ? [linearGradientDef(AREA_GRADIENT_ID, stops)] : []}
          fill={gradient ? [{ match: "*", id: AREA_GRADIENT_ID }] : []}
          enableGridX={false}
          enableGridY={false}
          axisLeft={null}
          axisRight={null}
          axisTop={null}
          axisBottom={null}
          isInteractive={interactive}
          useMesh={interactive}
          tooltip={({ point }) => (
            <LineTooltip>
              <strong>{point.data.xFormatted}</strong>
              <span>{commaNotation(point.data.y)}</span>
            </LineTooltip>
          )}
          animate
          motionConfig="gentle"
        />
      </Box>
      {ticks.length > 0 && (
        <TicksRow>
          {ticks.map((tick, i) => (
            <TickLabel key={`tick-${i}`}>{tick}</TickLabel>
          ))}
        </TicksRow>
      )}
    </Stack>
  );
};

const TicksRow = styled(Stack)({
  flexDirection: "row",
  justifyContent: "space-between",
  width: "100%"
});

const TickLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  fontSize: 10,
  lineHeight: "normal",
  fontFamily: "inter,sans-serif"
}));

const LineTooltip = styled("div")(({ theme }) => ({
  background: theme.palette.system[0],
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(0.75),
  border: `1px solid ${theme.palette.grey[200]}`,
  boxShadow: theme.shadows[2],
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  ...theme.typography.caption,
  color: theme.palette.text[800]
}));

export default HyroLineChart;
